import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { emptyEventsCache, loadEvents, deleteEvent } from "../../../api/events";
import { toast, ToastContainer } from 'react-toastify';
import DataTable from 'react-data-table-component';
import 'react-toastify/dist/ReactToastify.css';
import './EventsPage.css';
import { ROUTES } from "../../../config/routes";
import { getDefaultImage } from '../../../utils/eventUtils';
import { formatDateWithOptionalTime } from '../../../utils/dateUtils';
import ExcelJS from 'exceljs';
import EventsMap from '../../maps/EventsMap';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [eventIdToDelete, setEventIdToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [upcomingEventsCount, setUpcomingEventsCount] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [duplicateEvents, setDuplicateEvents] = useState([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState({});
  const [duplicatesToDelete, setDuplicatesToDelete] = useState([]);
  const [filterTitle, setFilterTitle] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterDescription, setFilterDescription] = useState("");
  const [filterSpeaker, setFilterSpeaker] = useState("");
  const [filterOrganizer, setFilterOrganizer] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  const history = useHistory();

  // Safe string normalization helpers
  const toLowerSafe = (value) => {
    if (value == null) return '';
    if (Array.isArray(value)) return value.join(' ').toLowerCase();
    return String(value).toLowerCase();
  };
  const norm = (v) => toLowerSafe(v).trim();

  useEffect(() => {
    fetchEvents();
  }, []);
  
  const fetchEvents = async () => {
    try {
      const startingDate = new Date();
      startingDate.setMonth(startingDate.getMonth() - 3);
      const { eventsData } = await loadEvents(null, undefined, startingDate, false);
      if (eventsData) {
        const uniqueEvents = Array.from(new Map(eventsData.map(event => [event.id, event])).values());
        setEvents(uniqueEvents);
        
        const now = new Date();
        const upcoming = uniqueEvents.filter(event => {
          const eventDate = new Date(event.endDate);
          return eventDate >= now;
        });
        setUpcomingEventsCount(upcoming.length);
      } else {
        setEvents([]);
        setUpcomingEventsCount(0);
      }
    } catch (error) {
      console.error('Error fetching events: ', error);
      setEvents([]);
      setUpcomingEventsCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    history.push(ROUTES.CREATE_EVENT.path + `?eid=${id}`);
  };

  const handleDelete = async () => {
    try {
      if (eventIdToDelete) {
        setLoading(true);

        await deleteEvent(eventIdToDelete);
        setEvents(events.filter(event => event.id !== eventIdToDelete));
        toast.success('Evènement supprimé avec succès.');
        emptyEventsCache();
        setEventIdToDelete(null);
      }
    } catch (error) {
      toast.error('Echec de suppression de l\'évènement. Essayez plus tard.');
      console.error('Error deleting event: ', error);
    } finally {
      setLoading(false);
      const modal = window.bootstrap.Modal.getInstance(document.getElementById('deleteModal'));
      modal.hide();
    }
  };

  const handleDeleteSelected = async () => {
    try {
      setLoading(true);
      
      const deletePromises = selectedRows.map(event => deleteEvent(event.id));
      
      await Promise.all(deletePromises);
      
      const updatedEvents = events.filter(event => 
        !selectedRows.some(selectedEvent => selectedEvent.id === event.id)
      );
      
      setEvents(updatedEvents);
      setSelectedRows([]);
      emptyEventsCache();
      toast.success(`${selectedRows.length} évènement(s) supprimé(s) avec succès.`);
      
    } catch (error) {
      toast.error('Échec de suppression des évènements sélectionnés. Essayez plus tard.');
      console.error('Error deleting selected events: ', error);
    } finally {
      setLoading(false);
    }
  };

  const showMultiDeleteModal = () => {
    const modal = new window.bootstrap.Modal(document.getElementById('multiDeleteModal'));
    modal.show();
  };

  const showModal = (eventId) => {
    setEventIdToDelete(eventId);
    const modal = new window.bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
  };

  const handleCreateEvent = () => {
    history.push(ROUTES.CREATE_EVENT.path);
  };

  const handleImportEvents = () => {
    history.push(ROUTES.IMPORT_EVENTS.path);
  };

  const handleBackToDashboard = () => {
    history.push(ROUTES.DASHBOARD.path);
  };

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Events');
    
    worksheet.columns = [
      { header: 'Title', key: 'title', width: 30 },
      { header: 'Date', key: 'date', width: 20 },
      { header: 'EndDate', key: 'endDate', width: 20 },
      { header: 'Location', key: 'location', width: 25 },
      { header: 'Description', key: 'description', width: 40 },
      { header: 'Speaker', key: 'speaker', width: 25 },
      { header: 'Organizer', key: 'organizer', width: 25 },
      { header: 'Category', key: 'category', width: 20 },
    ];
    
    events.forEach((event) => {
      const startDate = formatDateWithOptionalTime(event?.date);
      const endDate = event?.endDate ? formatDateWithOptionalTime(event.endDate) : '';
      
      worksheet.addRow({
        title: event.title || '',
        date: startDate || '',
        endDate: endDate || '',
        location: event.location.postalCode ? 
          `${event.location.cityName} (${event.location.postalCode})` : 
          event.location.cityName,
        description: event.description || '',
        speaker: event.speaker || '',
        organizer: event.organizer || '',
        category: event.category || '',
      });
    });
    
    worksheet.getRow(1).font = { bold: true };
    
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: 8 },
    };
    
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'events_export.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Export Excel réussi');
  };

  const toggleView = (mode) => {
    setViewMode(mode);
  };

  const handleRowSelected = ({ selectedRows }) => {
    setSelectedRows(selectedRows);
  };

  const CustomCheckbox = React.forwardRef(({ onClick, checked, ...rest }, ref) => (
    <div className="form-check d-flex justify-content-center align-items-center h-100">
      <input
        type="checkbox"
        className="form-check-input"
        checked={checked}
        ref={ref}
        onClick={onClick}
        style={{
          cursor: 'pointer',
          width: '20px',
          height: '20px'
        }}
        {...rest}
      />
    </div>
  ));

  const findDuplicateEvents = () => {
    const parseDateSafe = (value) => {
      if (!value) return null;
      const dt = new Date(value);
      return Number.isNaN(dt.getTime()) ? null : dt;
    };

    const dateKey = (value) => {
      const dt = parseDateSafe(value);
      if (!dt) return '';
      return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
    };

    const getRange = (e) => {
      const startDate = parseDateSafe(e?.date);
      const endDate = parseDateSafe(e?.endDate) || startDate;
      if (!startDate) {
        return { start: null, end: null, duration: 0 };
      }
      const safeEnd = endDate || startDate;
      return {
        start: startDate.getTime(),
        end: safeEnd.getTime(),
        duration: Math.max(0, safeEnd.getTime() - startDate.getTime()),
      };
    };

    const isDateIncluded = (e1, e2) => {
      const r1 = getRange(e1);
      const r2 = getRange(e2);
      if (r1.start == null || r2.start == null) return false;
      const r1InR2 = r1.start >= r2.start && r1.end <= r2.end;
      const r2InR1 = r2.start >= r1.start && r2.end <= r1.end;
      return r1InR2 || r2InR1;
    };

    const hasDateOverlap = (e1, e2) => {
      const r1 = getRange(e1);
      const r2 = getRange(e2);
      if (r1.start == null || r2.start == null) return false;
      return Math.max(r1.start, r2.start) <= Math.min(r1.end, r2.end);
    };

    const dateGapDays = (e1, e2) => {
      const r1 = getRange(e1);
      const r2 = getRange(e2);
      if (r1.start == null || r2.start == null) return Number.POSITIVE_INFINITY;
      const dayMs = 24 * 60 * 60 * 1000;
      const gap1 = Math.abs(r1.start - r2.end) / dayMs;
      const gap2 = Math.abs(r2.start - r1.end) / dayMs;
      return Math.min(gap1, gap2);
    };

    const normalizeTitleForDedup = (title) => {
      const base = norm(title)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[‐‑‒–—―-]/g, '-')
        .replace(/[’']/g, ' ')
        .replace(/[^\w\s\u0600-\u06FF-]/g, ' ')
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return base;
    };

    const semanticStopwords = new Set([
      'le', 'la', 'les', 'de', 'des', 'du', 'd', 'et', 'ou', 'au', 'aux', 'a', 'en', 'pour', 'sur', 'avec',
      'mosquee', 'mosquée', 'association', 'institut', 'session',
      'inscription', 'inscriptions', 'inscrire', 'inscrivez',
      'stage', 'atelier', 'cours', 'conference', 'conférence', 'khutbah', 'khotba', 'joumouaa', 'ljoumouaa',
    ]);

    const semanticTitleKey = (title) => {
      const normalized = normalizeTitleForDedup(title);
      if (!normalized) return '';
      const tokens = normalized
        .split(/\s+/)
        .filter((token) => token && !semanticStopwords.has(token) && token.length > 2);
      if (tokens.length < 2) return '';
      return [...new Set(tokens)].sort().join(' ');
    };

    const normalizeCategory = (category) => norm(category);

    const isSeriesLikeEvent = (event) => {
      const text = `${norm(event?.title)} ${norm(event?.description)}`.trim();
      if (!text) return false;
      const patterns = [
        /\bjour\s*\d+\b/i,
        /\bnuit\s*impaire\b/i,
        /\bpriere\s+du\s+ramadan\b/i,
        /\bpriere\s+de\s+ramadan\b/i,
        /\btous\s+les\s+jours\b/i,
        /\bchaque\s+jour\b/i,
        /\bquotidien\b/i,
        /\bsession\s*\d+\b/i,
      ];
      return patterns.some((re) => re.test(text));
    };

    const buildSemanticMergeKey = (event) => {
      if (isSeriesLikeEvent(event)) return '';
      const mosqueUrl = norm(event?.mosqueUrl);
      const skey = semanticTitleKey(event?.title);
      const startDate = parseDateSafe(event?.date);
      if (!mosqueUrl || !skey || !startDate) return '';
      const bucket = `${startDate.getUTCFullYear()}-${String(startDate.getUTCMonth() + 1).padStart(2, '0')}`;
      return `semantic|${mosqueUrl}|${bucket}|${skey}`;
    };

    const buildEventFingerprintKeys = (event) => {
      const keys = [];
      const mosqueUrl = norm(event?.mosqueUrl);
      const organizer = norm(event?.organizer);
      const city = norm(event?.location?.cityName);
      const titleNorm = normalizeTitleForDedup(event?.title);
      const notificationHash = norm(event?.notificationHash);
      const day = dateKey(event?.date);
      const hasPreciseDate = event?.hasPreciseDate !== false;

      if (notificationHash && day && titleNorm) {
        keys.push(`hash_title_day|${mosqueUrl}|${day}|${notificationHash}|${titleNorm}`);
      } else if (notificationHash && day) {
        keys.push(`hash_day|${mosqueUrl}|${day}|${notificationHash}`);
      }
      if (notificationHash && titleNorm) {
        keys.push(`hash_title|${mosqueUrl}|${notificationHash}|${titleNorm}`);
      }
      if (titleNorm && day) {
        keys.push(`title_day|${mosqueUrl}|${day}|${titleNorm}`);
      }
      if (titleNorm && day && (organizer || city)) {
        keys.push(`title_day_scope|${organizer}|${city}|${day}|${titleNorm}`);
      }
      if (!hasPreciseDate) {
        if (notificationHash) keys.push(`hash_noprecise|${mosqueUrl}|${notificationHash}`);
        if (titleNorm) keys.push(`title_noprecise|${mosqueUrl}|${titleNorm}`);
      }

      return keys;
    };

    const fingerprintIntersect = (e1, e2) => {
      const k1 = buildEventFingerprintKeys(e1);
      const k2 = buildEventFingerprintKeys(e2);
      if (!k1.length || !k2.length) return false;
      const s2 = new Set(k2);
      return k1.some((key) => s2.has(key));
    };

    const isDuplicateByScopeAndDate = (e1, e2) => {
      const org1 = norm(e1?.organizer);
      const org2 = norm(e2?.organizer);
      const city1 = norm(e1?.location?.cityName);
      const city2 = norm(e2?.location?.cityName);
      const title1 = norm(e1?.title);
      const title2 = norm(e2?.title);
      if (!org1 || !city1 || !title1) return false;
      if (!(org1 === org2 && city1 === city2 && title1 === title2)) return false;
      return isDateIncluded(e1, e2);
    };

    const isDuplicateBySemanticFallback = (e1, e2) => {
      const hash1 = norm(e1?.notificationHash);
      const hash2 = norm(e2?.notificationHash);
      if (hash1 && hash2 && hash1 !== hash2) {
        return false;
      }
      const k1 = buildSemanticMergeKey(e1);
      const k2 = buildSemanticMergeKey(e2);
      return Boolean(k1 && k2 && k1 === k2);
    };

    const isDuplicateInSameNotification = (e1, e2) => {
      const hash1 = norm(e1?.notificationHash);
      const hash2 = norm(e2?.notificationHash);
      if (!hash1 || !hash2 || hash1 !== hash2) return false;

      const cat1 = normalizeCategory(e1?.category);
      const cat2 = normalizeCategory(e2?.category);
      if (cat1 && cat2 && cat1 !== cat2) return false;

      const title1 = normalizeTitleForDedup(e1?.title);
      const title2 = normalizeTitleForDedup(e2?.title);
      const sem1 = semanticTitleKey(e1?.title);
      const sem2 = semanticTitleKey(e2?.title);
      const titleMatch = Boolean(title1 && title2 && (title1 === title2 || title1.includes(title2) || title2.includes(title1)));
      const semMatch = Boolean(sem1 && sem2 && sem1 === sem2);

      if (hasDateOverlap(e1, e2) && (titleMatch || semMatch)) return true;
      if (dateGapDays(e1, e2) <= 2 && (titleMatch || semMatch)) return true;
      return false;
    };

    const areExtractorStyleDuplicates = (e1, e2) => {
      if (isDuplicateInSameNotification(e1, e2)) return true;
      if (fingerprintIntersect(e1, e2)) return true;
      if (isDuplicateByScopeAndDate(e1, e2)) return true;
      if (isDuplicateBySemanticFallback(e1, e2)) return true;
      return false;
    };

    const parent = events.map((_, idx) => idx);
    const findParent = (x) => {
      if (parent[x] !== x) parent[x] = findParent(parent[x]);
      return parent[x];
    };
    const union = (a, b) => {
      const pa = findParent(a);
      const pb = findParent(b);
      if (pa !== pb) parent[pb] = pa;
    };

    for (let i = 0; i < events.length; i += 1) {
      for (let j = i + 1; j < events.length; j += 1) {
        if (areExtractorStyleDuplicates(events[i], events[j])) {
          union(i, j);
        }
      }
    }

    const groupsByRoot = new Map();
    events.forEach((event, index) => {
      const root = findParent(index);
      if (!groupsByRoot.has(root)) {
        groupsByRoot.set(root, []);
      }
      groupsByRoot.get(root).push(event);
    });

    const duplicates = Array.from(groupsByRoot.values()).filter((group) => group.length > 1);
    
    // Sort groups to keep the "best" one first (Widest date range, then longest description)
    duplicates.forEach(group => {
        group.sort((a, b) => {
            const rA = getRange(a);
            const rB = getRange(b);
            if (rB.duration !== rA.duration) return rB.duration - rA.duration;
            // Tie-breaker: Description length
            const descA = (a.description || "").length;
            const descB = (b.description || "").length;
            return descB - descA;
        });
    });

    const initialSelectedState = {};
    duplicates.forEach((group, groupIndex) => {
      initialSelectedState[groupIndex] = group.slice(1).map(event => ({
        id: event.id,
        selected: true
      }));
    });
    
    setSelectedDuplicates(initialSelectedState);
    setDuplicateEvents(duplicates);
    
    if (duplicates.length > 0) {
      const modal = new window.bootstrap.Modal(document.getElementById('duplicatesModal'));
      modal.show();
    } else {
      toast.info('Aucun événement en doublon détecté.');
    }
  };

  const handleDuplicateCheckboxChange = (groupIndex, eventId) => {
    setSelectedDuplicates(prevState => {
      const updatedGroup = [...prevState[groupIndex]];
      const eventIndex = updatedGroup.findIndex(item => item.id === eventId);
      
      if (eventIndex !== -1) {
        updatedGroup[eventIndex] = {
          ...updatedGroup[eventIndex],
          selected: !updatedGroup[eventIndex].selected
        };
      }
      
      return {
        ...prevState,
        [groupIndex]: updatedGroup
      };
    });
  };

  const toggleGroupSelection = (groupIndex, selectAll) => {
    setSelectedDuplicates(prevState => {
      const updatedGroup = prevState[groupIndex].map(item => ({
        ...item,
        selected: selectAll
      }));
      
      return {
        ...prevState,
        [groupIndex]: updatedGroup
      };
    });
  };

  const toggleAllDuplicatesSelection = (selectAll) => {
    setSelectedDuplicates(prevState => {
      const updatedState = { ...prevState };
      
      Object.keys(updatedState).forEach(groupIndex => {
        updatedState[groupIndex] = updatedState[groupIndex].map(item => ({
          ...item,
          selected: selectAll
        }));
      });
      
      return updatedState;
    });
  };

  const prepareDuplicatesForDeletion = () => {
    const eventsToDelete = [];
    
    Object.keys(selectedDuplicates).forEach(groupIndex => {
      selectedDuplicates[groupIndex].forEach(item => {
        if (item.selected) {
          const event = duplicateEvents[parseInt(groupIndex)].find(e => e.id === item.id);
          if (event) {
            eventsToDelete.push(event);
          }
        }
      });
    });
    
    if (eventsToDelete.length === 0) {
      toast.info('Aucun doublon sélectionné pour suppression.');
      return;
    }
    
    setDuplicatesToDelete(eventsToDelete);
    
    const modal = new window.bootstrap.Modal(document.getElementById('deleteDuplicatesModal'));
    modal.show();
  };

  const deleteSelectedDuplicates = async () => {
    try {
      setLoading(true);
      
      const deletePromises = duplicatesToDelete.map(event => deleteEvent(event.id));
      
      await Promise.all(deletePromises);
      
      const updatedEvents = events.filter(event => 
        !duplicatesToDelete.some(deletedEvent => deletedEvent.id === event.id)
      );
      
      setEvents(updatedEvents);
      emptyEventsCache();
      
      const duplicatesModal = window.bootstrap.Modal.getInstance(document.getElementById('duplicatesModal'));
      const confirmationModal = window.bootstrap.Modal.getInstance(document.getElementById('deleteDuplicatesModal'));
      
      if (duplicatesModal) duplicatesModal.hide();
      if (confirmationModal) confirmationModal.hide();
      
      toast.success(`${duplicatesToDelete.length} doublon(s) supprimé(s) avec succès.`);
      
      setDuplicatesToDelete([]);
      
    } catch (error) {
      toast.error('Échec de suppression des doublons. Essayez plus tard.');
      console.error('Error deleting duplicates: ', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(event => {
    const titleMatch = toLowerSafe(event.title).includes(toLowerSafe(filterTitle));
    const dateString = `${formatDateWithOptionalTime(event.date)}${event.endDate ? ' ' + formatDateWithOptionalTime(event.endDate) : ''}`;
    const dateMatch = toLowerSafe(dateString).includes(toLowerSafe(filterDate));
    const locationString = event.location?.postalCode
      ? `${event.location?.cityName ?? ''} (${event.location?.postalCode ?? ''})`
      : (event.location?.cityName ?? '');
    const locationMatch = toLowerSafe(locationString).includes(toLowerSafe(filterLocation));
    const descriptionMatch = toLowerSafe(event.description).includes(toLowerSafe(filterDescription));
    const speakerMatch = toLowerSafe(event.speaker).includes(toLowerSafe(filterSpeaker));
    const organizerMatch = toLowerSafe(event.organizer).includes(toLowerSafe(filterOrganizer));
    const categoryMatch = toLowerSafe(event.category).includes(toLowerSafe(filterCategory));
    return (
      titleMatch &&
      dateMatch &&
      locationMatch &&
      descriptionMatch &&
      speakerMatch &&
      organizerMatch &&
      categoryMatch
    );
  });

  const columns = [
    {
      name: '',
      cell: row => <div />,
      width: '10px',
      sortable: false,
      selector: row => row,
    },
    {
      name: '#',
      selector: (row, index) => index + 1,
      sortable: true,
      width: '40px',
    },
    {
      name: 'Image',
      cell: (row) => <img src={getDefaultImage(row)} alt="Event" className="event-image" style={{ width: "100px", height: "auto" }} />,
    },
    {
      name: 'Titre',
      selector: (row) => row.title,
      sortable: true,
    },
    {
      name: 'Date',
      cell: (row) => {
        const startDate = formatDateWithOptionalTime(row?.date);
        if (!row?.endDate) return startDate;
        return (
          <div>
            Du {startDate}<br />
            Au {formatDateWithOptionalTime(row.endDate)}
          </div>
        );
      },
      sortable: true,
      sortFunction: (a, b) => new Date(a.date) - new Date(b.date),
      width: '100px',
    },
    {
      name: 'Location',
      selector: (row) =>
        row.location?.postalCode
          ? `${row.location?.cityName ?? ''} (${row.location?.postalCode ?? ''})`
          : (row.location?.cityName ?? ''),
      sortable: true,
    },
    {
      name: 'Description',
      cell: (row) => <textarea rows="5" value={row.description} readOnly className="form-control-plaintext" />,
    },
    {
      name: 'Speaker',
      selector: (row) => row.speaker || 'N/A',
      sortable: true,
    },
    {
      name: 'Organizer',
      selector: (row) => row.organizer || 'N/A',
      sortable: true,
    },
    {
      name: 'Category',
      selector: (row) => row.category || 'N/A',
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div key={`action-${row.id}`} className="submit-btn action-buttons" style={{ marginBottom: "1rem", minWidth: '150px' }}>
          <button className="btn btn-primary btn-action" onClick={() => handleEdit(row.id)} title="Editer">
            <i className="bi bi-pencil"></i>
          </button>
          <button className="btn btn-danger btn-action" onClick={() => showModal(row.id)} title="Supprimer">
            <i className="bi bi-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  const customStyles = {
    table: {
      style: {
        width: 'calc(100% + 50px)',
      },
    },
    header: {
      style: {
        minHeight: '56px',
        backgroundColor: '#e3f2fd',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#e3f2fd',
      },
    },
    headCells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    },
    cells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    },
    rows: {
      style: {
        minHeight: '50px',
        '&:nth-of-type(odd)': {
          backgroundColor: '#f1f8ff',
        },
      },
    },
  };

  return (
    <div className="events-wrapper overflow-hidden">
      <ToastContainer />
      <div className="container pt-120 position-relative">
        <div className="row">
          <div className="col-lg-8"></div>
          <div className="events-container">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h1>Evènements</h1>
              <div className="badge bg-primary fs-6">
                {upcomingEventsCount} événements à venir
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
              <div>
                <button className="btn btn-primary me-2" onClick={handleCreateEvent}>
                  <i className="bi bi-plus" title="Créer un évènement"></i>
                </button>
                <button className="btn btn-primary me-2" onClick={handleImportEvents}>
                  <i className="bi bi-upload me-2" title="Importer évènements"></i>
                  Importer Excel
                </button>
                <button className="btn btn-success me-2" onClick={handleExportExcel}>
                  <i className="bi bi-file-excel me-1"></i>
                  Exporter Excel
                </button>
                <button className="btn btn-warning me-2" onClick={findDuplicateEvents} title="Rechercher les doublons">
                  <i className="bi bi-search me-1"></i>
                  Détecter doublons
                </button>
                {selectedRows.length > 0 && (
                  <>
                    <button className="btn btn-danger me-2" onClick={showMultiDeleteModal}>
                      <i className="bi bi-trash me-1"></i>
                      Supprimer sélection
                    </button>
                    <span className="ms-2 badge bg-info">
                      {selectedRows.length} élément{selectedRows.length > 1 ? 's' : ''} sélectionné{selectedRows.length > 1 ? 's' : ''}
                    </span>
                  </>
                )}
              </div>
              
              <div className="btn-group" role="group">
                <button 
                  type="button" 
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => toggleView('table')}
                >
                  <i className="bi bi-table me-1"></i>
                  Tableau
                </button>
                <button 
                  type="button" 
                  className={`btn ${viewMode === 'map' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => toggleView('map')}
                >
                  <i className="bi bi-map me-1"></i>
                  Carte
                </button>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-2">
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 150 }}
                placeholder="Filtrer Titre"
                value={filterTitle}
                onChange={e => setFilterTitle(e.target.value)}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 150 }}
                placeholder="Filtrer Date"
                value={filterDate}
                onChange={e => setFilterDate(e.target.value)}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 150 }}
                placeholder="Filtrer Lieu"
                value={filterLocation}
                onChange={e => setFilterLocation(e.target.value)}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 150 }}
                placeholder="Filtrer Description"
                value={filterDescription}
                onChange={e => setFilterDescription(e.target.value)}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 150 }}
                placeholder="Filtrer Intervenant"
                value={filterSpeaker}
                onChange={e => setFilterSpeaker(e.target.value)}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 150 }}
                placeholder="Filtrer Organisateur"
                value={filterOrganizer}
                onChange={e => setFilterOrganizer(e.target.value)}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 150 }}
                placeholder="Filtrer Catégorie"
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
              />
            </div>

            {loading ? (
              <div>Loading...</div>
            ) : viewMode === 'table' ? (
              <DataTable
                columns={columns}
                data={filteredEvents}
                pagination
                highlightOnHover
                responsive
                striped
                dense
                customStyles={customStyles}
                keyField="id"
                selectableRows
                selectableRowsHighlight
                selectableRowsComponent={CustomCheckbox}
                selectableRowsComponentProps={{}} 
                onSelectedRowsChange={handleRowSelected}
              />
            ) : (
              <EventsMap 
                events={filteredEvents} 
                handleEdit={handleEdit} 
                showModal={showModal}
              />
            )}
            
            <div className="submit-btn mt-3">
              <button className="btn btn-secondary pagination-button" onClick={handleBackToDashboard}>
                <i className="bi bi-arrow-left" title="Retour au dashboard"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="modal fade" id="deleteModal" tabIndex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="deleteModalLabel">Confirmation Suppression</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              Êtes-vous sûr de vouloir supprimer cet évènement ?
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                <i className="bi bi-x-circle" title="Annuler"></i>
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDelete}>
                <i className="bi bi-trash" title="Supprimer"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="multiDeleteModal" tabIndex="-1" aria-labelledby="multiDeleteModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="multiDeleteModalLabel">Confirmation Suppression Multiple</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              Êtes-vous sûr de vouloir supprimer {selectedRows.length} évènement{selectedRows.length > 1 ? 's' : ''} sélectionné{selectedRows.length > 1 ? 's' : ''} ?
              <div className="alert alert-warning mt-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Cette action est irréversible.
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                <i className="bi bi-x-circle me-1"></i>
                Annuler
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteSelected}>
                <i className="bi bi-trash me-1"></i>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="modal fade" id="duplicatesModal" tabIndex="-1" aria-labelledby="duplicatesModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="duplicatesModalLabel">
                Événements en doublon potentiels
              </h5>
              <div className="d-flex align-items-center gap-2">
                <button 
                  type="button" 
                  className="btn btn-danger btn-sm" 
                  onClick={prepareDuplicatesForDeletion}
                  disabled={duplicateEvents.length === 0}
                  title="Valider et supprimer la sélection"
                >
                  <i className="bi bi-trash me-1"></i>
                  Valider
                </button>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
            </div>
            <div className="modal-body">
              {duplicateEvents.length === 0 ? (
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  Aucun événement en doublon détecté.
                </div>
              ) : (
                <>
                  <div className="alert alert-warning mb-2">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {duplicateEvents.length} groupe(s) de doublons potentiels trouvé(s).
                    <div className="mt-2 small">
                      <strong>Note:</strong> Pour chaque groupe, le premier événement est conservé par défaut et les autres sont présélectionnés pour suppression.
                    </div>
                  </div>
                  
                  <div className="d-flex justify-content-end mb-3">
                    <button 
                      className="btn btn-outline-primary btn-sm me-2"
                      onClick={() => toggleAllDuplicatesSelection(true)}
                      title="Sélectionner tous les doublons"
                    >
                      <i className="bi bi-check-all me-1"></i>
                      Tout sélectionner
                    </button>
                    <button 
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => toggleAllDuplicatesSelection(false)}
                      title="Désélectionner tous les doublons"
                    >
                      <i className="bi bi-eraser me-1"></i>
                      Tout désélectionner
                    </button>
                  </div>
                  
                  {duplicateEvents.map((group, groupIndex) => (
                    <div key={`duplicate-group-${groupIndex}`} className="duplicate-group mb-4 pb-3 border-bottom">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6>Groupe de doublons #{groupIndex + 1}</h6>
                        <div>
                          <button 
                            className="btn btn-outline-secondary btn-sm me-2"
                            onClick={() => toggleGroupSelection(groupIndex, true)}
                            title="Sélectionner tous les doublons de ce groupe"
                          >
                            <i className="bi bi-check-all me-1"></i>
                            Sélectionner groupe
                          </button>
                          <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => toggleGroupSelection(groupIndex, false)}
                            title="Désélectionner tous les doublons de ce groupe"
                          >
                            <i className="bi bi-x me-1"></i>
                            Désélectionner groupe
                          </button>
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm table-hover">
                          <thead className="table-light">
                            <tr>
                              <th scope="col" width="40px">#</th>
                              <th scope="col" width="60px">Action</th>
                              <th scope="col">Titre</th>
                              <th scope="col">Date</th>
                              <th scope="col">Date de fin</th>
                              <th scope="col">Lieu</th>
                              <th scope="col">Organisateur</th>
                              <th scope="col">Intervenant</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr key={`duplicate-${groupIndex}-${group[0].id}`} className="table-success">
                              <td>1</td>
                              <td>
                                <span className="badge bg-success">Conserver</span>
                              </td>
                              <td>{group[0].title}</td>
                              <td>{formatDateWithOptionalTime(group[0].date)}</td>
                              <td>{group[0].endDate ? formatDateWithOptionalTime(group[0].endDate) : '-'}</td>
                              <td>{group[0].location.cityName}</td>
                              <td>{group[0].organizer || 'N/A'}</td>
                              <td>{group[0].speaker || 'N/A'}</td>
                            </tr>
                            
                            {group.slice(1).map((event, eventIndex) => {
                              const isSelected = selectedDuplicates[groupIndex]?.find(
                                item => item.id === event.id
                              )?.selected || false;
                              
                              return (
                                <tr 
                                  key={`duplicate-${groupIndex}-${event.id}`}
                                  className={isSelected ? "table-danger" : ""}
                                >
                                  <td>{eventIndex + 2}</td>
                                  <td>
                                    <div className="form-check">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={isSelected}
                                        onChange={() => handleDuplicateCheckboxChange(groupIndex, event.id)}
                                        id={`duplicate-check-${groupIndex}-${event.id}`}
                                      />
                                      <label 
                                        className="form-check-label" 
                                        htmlFor={`duplicate-check-${groupIndex}-${event.id}`}
                                      >
                                        Supprimer
                                      </label>
                                    </div>
                                  </td>
                                  <td>{event.title}</td>
                                  <td>{formatDateWithOptionalTime(event.date)}</td>
                                  <td>{event.endDate ? formatDateWithOptionalTime(event.endDate) : '-'}</td>
                                  <td>{event.location.cityName}</td>
                                  <td>{event.organizer || 'N/A'}</td>
                                  <td>{event.speaker || 'N/A'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                <i className="bi bi-x-circle me-1"></i>
                Annuler
              </button>
              <button 
                type="button" 
                className="btn btn-danger" 
                onClick={prepareDuplicatesForDeletion}
                disabled={duplicateEvents.length === 0}
              >
                <i className="bi bi-trash me-1"></i>
                Supprimer sélection
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="modal fade" id="deleteDuplicatesModal" tabIndex="-1" aria-labelledby="deleteDuplicatesModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="deleteDuplicatesModalLabel">Confirmation Suppression des Doublons</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <p>
                Êtes-vous sûr de vouloir supprimer {duplicatesToDelete.length} évènement{duplicatesToDelete.length > 1 ? 's' : ''} en doublon ?
              </p>
              <div className="alert alert-warning mt-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Cette action est irréversible.
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                <i className="bi bi-x-circle me-1"></i>
                Annuler
              </button>
              <button 
                type="button" 
                className="btn btn-danger" 
                onClick={deleteSelectedDuplicates}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Suppression en cours...
                  </>
                ) : (
                  <>
                    <i className="bi bi-trash me-1"></i>
                    Confirmer suppression
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default EventsPage;


